import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, IndianRupee, RotateCcw, FileText, Plus, Loader2 } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { money, apiError, uid } from "@/lib/api";
import { consultantBalances, localToday } from "@/lib/payouts";
import { ConsultantForm, DeleteConsultantDialog } from "@/components/ConsultantFields";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import Stat from "@/components/Stat";

const PAYOUT_METHODS = ["Google Pay", "PhonePe", "Other UPI", "Bank transfer", "Cash", "Cheque"];
const DIALOG = "max-w-lg gap-5 rounded-2xl border-slate-200 bg-white p-6 text-slate-900 sm:rounded-2xl";
const PAYOUTS_SHOWN = 8;

export default function Consultants() {
  const { data, activeConsultants, restoreConsultant, isOwner, showMoney } = useApp();
  const [deleting, setDeleting] = useState(null);
  const [adding, setAdding] = useState(false);
  const [paying, setPaying] = useState(null); // null = closed, "" = pick a consultant, or a consultant id
  const [allPayouts, setAllPayouts] = useState(false);
  const archived = data.consultants.filter((c) => c.deleted);

  // Same records as the consultant report, so both always show the same balances.
  const balances = useMemo(() => consultantBalances(data.consultants, data.visits, data.settlements), [data.consultants, data.visits, data.settlements]);
  const payouts = useMemo(() => [...data.settlements].sort((a, b) => (b.date || "").localeCompare(a.date || "") || (b.updated_at || "").localeCompare(a.updated_at || "")), [data.settlements]);
  const totals = useMemo(() => balances.reduce((a, r) => ({ earned: a.earned + r.earned, paid: a.paid + r.paid, outstanding: a.outstanding + r.outstanding }),
    { earned: 0, paid: 0, outstanding: 0 }), [balances]);
  const nameOf = (id) => data.consultants.find((c) => c.id === id)?.name || "—";

  return (
    <div className="space-y-5" data-testid="consultants-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="dm-page-title">
            Visiting <span className="font-extrabold">consultants</span>
          </h1>
          <p className="mt-1 text-[13px] text-slate-500">{activeConsultants.length} active{archived.length ? `, ${archived.length} archived` : ""}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {showMoney && <Link to="/app/consultants/report" data-testid="consultant-report-link" className="dm-btn-ghost"><FileText size={15} /> Report</Link>}
          {showMoney && activeConsultants.length > 0 && (
            <button data-testid="open-payout" onClick={() => setPaying("")} className="dm-btn-ghost"><IndianRupee size={15} /> Record payout</button>
          )}
          <button data-testid="open-add-consultant" onClick={() => setAdding(true)} className="dm-btn-primary"><Plus size={15} /> Add consultant</button>
        </div>
      </div>

      {showMoney && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3" data-testid="consultant-totals">
          <Stat hero label="Owed to consultants" value={money(totals.outstanding)} className="col-span-2 md:col-span-1" />
          <Stat label="Fees earned" value={money(totals.earned)} />
          <Stat label="Paid out" value={money(totals.paid)} tone="good" />
        </div>
      )}

      <div className="dm-panel overflow-hidden">
        <div className="dm-panel-head">
          <h2 className="font-head text-lg font-semibold text-slate-800">Roster</h2>
          <span className="text-xs text-slate-500">{showMoney ? "Outstanding · earned · paid" : `${activeConsultants.length} active`}</span>
        </div>
        {activeConsultants.length === 0 ? (
          <div className="dm-empty m-4">
            No consultants yet. <button onClick={() => setAdding(true)} className="dm-link">Add your first consultant</button>
          </div>
        ) : (
          <ul className="max-h-[60vh] divide-y divide-slate-200 overflow-auto" data-testid="consultant-list">
            {activeConsultants.map((c) => {
              const row = balances.find((s) => s.consultant.id === c.id);
              return (
                <li key={c.id} className="flex items-center justify-between gap-4 px-4 py-2.5 transition-colors hover:bg-slate-50">
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-slate-800">{c.name}</p>
                    {(c.specialty || c.phone) && <p className="truncate text-xs text-slate-500">{[c.specialty, c.phone].filter(Boolean).join(" · ")}</p>}
                  </div>
                  <div className="flex shrink-0 items-center gap-3 md:gap-4">
                    {showMoney && row && (
                      <div className="text-right">
                        <p className={`font-mono text-sm font-semibold ${row.outstanding > 0 ? "text-red-700" : "text-slate-800"}`}>{money(row.outstanding)}</p>
                        <p className="hidden font-mono text-[11px] text-slate-500 sm:block">Earned {money(row.earned)} · paid {money(row.paid)}</p>
                      </div>
                    )}
                    {showMoney && (
                      <button data-testid={`pay-consultant-${c.id}`} onClick={() => setPaying(c.id)} className="dm-btn-ghost !px-3 !py-1 !text-xs">Pay</button>
                    )}
                    {isOwner && (
                      <button data-testid={`delete-consultant-${c.id}`} onClick={() => setDeleting(c)} aria-label={`Delete ${c.name}`} className="text-slate-500 transition-colors hover:text-red-600"><Trash2 size={16} /></button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {showMoney && payouts.length > 0 && (
        <div className="dm-panel overflow-hidden" data-testid="settlement-history">
          <div className="dm-panel-head">
            <h2 className="font-head text-lg font-semibold text-slate-800">Payouts</h2>
            <span className="text-xs text-slate-500">Paid by the clinic · separate from patient payments</span>
          </div>
          <ul className={`divide-y divide-slate-100 ${allPayouts ? "max-h-[60vh] overflow-auto" : ""}`}>
            {(allPayouts ? payouts : payouts.slice(0, PAYOUTS_SHOWN)).map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
                <span className="min-w-0">
                  <span className="block truncate font-semibold text-slate-800">{nameOf(p.consultant_id)}</span>
                  <span className="block truncate font-mono text-xs text-slate-500">{[p.date, p.method, p.note].filter(Boolean).join(" · ")}</span>
                </span>
                <span className="shrink-0 font-mono font-semibold text-slate-800">{money(p.amount)}</span>
              </li>
            ))}
          </ul>
          {payouts.length > PAYOUTS_SHOWN && (
            <button onClick={() => setAllPayouts(!allPayouts)} className="w-full border-t border-slate-100 py-2.5 text-xs font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-800">
              {allPayouts ? "Show fewer" : `Show all ${payouts.length} payouts`}
            </button>
          )}
        </div>
      )}

      {isOwner && archived.length > 0 && (
        <div className="dm-panel overflow-hidden" data-testid="archived-consultants">
          <div className="dm-panel-head">
            <h2 className="font-head text-lg font-semibold text-slate-800">Archived</h2>
            <span className="text-xs text-slate-500">History kept</span>
          </div>
          <ul className="divide-y divide-slate-200">
            {archived.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-4 px-5 py-3">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-slate-500">{c.name}</p>
                  <p className="truncate text-xs text-slate-400">{c.specialty || "—"}{c.phone ? ` · ${c.phone}` : ""}</p>
                </div>
                <button data-testid={`restore-consultant-${c.id}`} className="dm-btn-quiet !py-1.5 !text-xs"
                  onClick={async () => { try { await restoreConsultant(c.id); toast.success(`${c.name} restored`); } catch (err) { toast.error(apiError(err)); } }}>
                  <RotateCcw size={13} /> Restore
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className={DIALOG} data-testid="add-consultant-dialog">
          <DialogHeader>
            <DialogTitle className="font-head text-lg font-semibold text-slate-900">Add consultant</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">They'll appear in the visit form straight away.</DialogDescription>
          </DialogHeader>
          <ConsultantForm compact onSaved={() => setAdding(false)} />
        </DialogContent>
      </Dialog>

      <PayoutDialog open={paying !== null} consultantId={paying || ""} balances={balances} consultants={activeConsultants} onClose={() => setPaying(null)} />
      <DeleteConsultantDialog consultant={deleting} onClose={() => setDeleting(null)} />
    </div>
  );
}

function PayoutDialog({ open, consultantId, balances, consultants, onClose }) {
  const { save } = useApp();
  // Fresh id per entry: a double tap or retry re-saves the same payout instead of adding another.
  const blank = (cid) => ({ id: uid(), consultant_id: cid, amount: "", date: localToday(), method: "Google Pay", note: "" });
  const [f, setF] = useState(() => blank(consultantId));
  const [busy, setBusy] = useState(false);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) { setWasOpen(open); if (open) setF(blank(consultantId)); } // fresh entry each time it opens
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const owed = balances.find((b) => b.consultant.id === f.consultant_id)?.outstanding || 0;

  const submit = async (e) => {
    e.preventDefault();
    const amount = Number(f.amount || 0);
    if (!f.consultant_id) return toast.error("Choose a consultant");
    if (amount <= 0) return toast.error("Enter the amount paid");
    setBusy(true);
    try {
      await save("settlements", { ...f, amount, note: f.note.trim(), date: f.date || localToday() });
      toast.success(`${money(amount)} payout recorded`);
      onClose();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className={DIALOG} data-testid="payout-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-lg font-semibold text-slate-900">Record payout</DialogTitle>
          <DialogDescription className="text-sm text-slate-500">Money the clinic paid a consultant. It doesn't affect patient balances.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="settlement-form">
          <div className="col-span-2">
            <p className="label-xs mb-1.5">Consultant</p>
            <select data-testid="settlement-consultant-select" className="dm-input" value={f.consultant_id} onChange={set("consultant_id")}>
              <option value="">— Select —</option>
              {consultants.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {f.consultant_id && (
              <p className="mt-1.5 text-xs text-slate-500">
                Currently owed: <span className={`font-mono font-semibold ${owed > 0 ? "text-red-700" : "text-slate-700"}`}>{money(owed)}</span>
                {owed > 0 && <button type="button" onClick={() => setF({ ...f, amount: String(owed) })} className="dm-link ml-2">Pay in full</button>}
              </p>
            )}
          </div>
          <div>
            <p className="label-xs mb-1.5">Amount (₹)</p>
            <input type="number" min="0" data-testid="settlement-amount-input" className="dm-input font-mono" placeholder="0" value={f.amount} onChange={set("amount")} />
          </div>
          <div>
            <p className="label-xs mb-1.5">Date</p>
            <input type="date" data-testid="settlement-date-input" className="dm-input" value={f.date} onChange={set("date")} />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <p className="label-xs mb-1.5">Paid via</p>
            <select data-testid="settlement-method-select" className="dm-input" value={f.method} onChange={set("method")}>
              {PAYOUT_METHODS.map((m) => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <p className="label-xs mb-1.5">Note / transaction no.</p>
            <input data-testid="settlement-note-input" className="dm-input" placeholder="e.g. UPI ref 4321…" value={f.note} onChange={set("note")} />
          </div>
          <div className="col-span-2 flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} disabled={busy} className="dm-btn-ghost">Cancel</button>
            <button type="submit" data-testid="settlement-save-btn" disabled={busy} className="dm-btn-primary disabled:opacity-40">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <IndianRupee size={15} />} Record payout
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
